import {
  IDENTITY_METHODS,
  PRIVACY_RIGHTS,
  REQUEST_CHANNELS,
  REQUEST_EXTENSION_DAYS,
  REQUEST_OUTCOMES,
  REQUEST_PREVENTION_DAYS,
  REQUEST_SEARCH_CRITERIA,
  formatDateTime,
  type IdentityMethod,
  type PrivacyRight,
  type RequestOutcome,
  type RequestSearchCriterion,
} from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router';

import { AnonymizeDialog, ExportDialog } from '../../components/privacy/contact-rights.tsx';
import { RegisterRequestDialog, RequestStatusBadge } from '../../components/privacy/requests.tsx';
import { Alert } from '../../components/ui/alert.tsx';
import { Badge } from '../../components/ui/badge.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Card } from '../../components/ui/card.tsx';
import { Dialog } from '../../components/ui/dialog.tsx';
import { Field, Input, Select } from '../../components/ui/field.tsx';
import { Loading } from '../../components/ui/skeleton.tsx';
import { api, ApiError, errorMessage } from '../../lib/api.ts';
import {
  daysLeftLabel,
  formatDueOn,
  OPEN_STATUSES,
  refreshRequests,
  requestKeys,
  usePrivacySettings,
  useRequest,
  useRequests,
  type RequestDetail,
  type RequestSearchMatch,
} from '../../lib/privacy-requests-api.ts';
import { useSession } from '../../lib/session.ts';
import { useToast } from '../../lib/toast.tsx';
import { NotFoundPage } from '../status-pages.tsx';

const textarea = 'w-full rounded-lg border border-border bg-surface p-3 text-base sm:text-sm';

// ---------------------------------------------------------------------------
// The list (on /admin/privacidad)
// ---------------------------------------------------------------------------

export function RequestsSection() {
  const [status, setStatus] = useState('open');
  const [right, setRight] = useState('');
  const [page, setPage] = useState(1);
  const [registering, setRegistering] = useState(false);
  const requests = useRequests(status, right, page);
  const pages = Math.max(1, Math.ceil((requests.data?.total ?? 0) / 25));
  return (
    <Card className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-semibold">Solicitudes ARCO-POL</h2>
          <p className="text-sm text-muted-foreground">
            Acceso, rectificación, cancelación, oposición, portabilidad, olvido y limitación.
            Cualquier usuario registra la recepción; aquí se tramitan.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            setRegistering(true);
          }}
        >
          <Plus /> Registrar solicitud
        </Button>
      </div>
      <div className="grid gap-2 sm:flex">
        <Select
          aria-label="Estado"
          className="sm:w-48"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="open">Abiertas</option>
          <option value="">Todas</option>
          <option value="resolved">Resueltas</option>
          <option value="withdrawn">Desistidas</option>
          <option value="incomplete">No completadas</option>
        </Select>
        <Select
          aria-label="Derecho"
          className="sm:w-48"
          value={right}
          onChange={(e) => {
            setRight(e.target.value);
            setPage(1);
          }}
        >
          <option value="">Todos los derechos</option>
          {(Object.keys(PRIVACY_RIGHTS) as PrivacyRight[]).map((r) => (
            <option key={r} value={r}>
              {PRIVACY_RIGHTS[r].label}
            </option>
          ))}
        </Select>
      </div>
      {requests.isPending ? (
        <Loading />
      ) : requests.error ? (
        <Alert tone="danger">{errorMessage(requests.error)}</Alert>
      ) : requests.data.items.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {status === 'open' ? 'No hay solicitudes abiertas.' : 'No hay solicitudes.'}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {requests.data.items.map((r) => {
            const late = r.daysLeft !== null && r.daysLeft < 0;
            return (
              <li
                key={r.id}
                className="flex flex-col gap-1 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="space-y-0.5">
                  <p className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/admin/privacidad/solicitudes/${r.id}`}
                      className="font-medium text-primary hover:underline"
                    >
                      N.º {r.folio} · {PRIVACY_RIGHTS[r.right].label}
                    </Link>
                    <RequestStatusBadge status={r.status} />
                    {r.extended && <Badge>Prorrogada</Badge>}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {r.requesterName}
                    {r.contact ? ` · ${r.contact.name}` : ''} · {REQUEST_CHANNELS[r.channel]}
                  </p>
                </div>
                <p className={late ? 'text-sm font-medium text-danger' : 'text-sm'}>
                  {r.closedAt
                    ? r.outcome
                      ? REQUEST_OUTCOMES[r.outcome]
                      : ''
                    : r.status === 'in_prevention'
                      ? 'En prevención'
                      : `${daysLeftLabel(r.daysLeft) ?? ''} (${formatDueOn(r.dueOn)})`}
                </p>
              </li>
            );
          })}
        </ul>
      )}
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <Button
            size="sm"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => {
              setPage(page - 1);
            }}
          >
            Anterior
          </Button>
          <span>
            {page} de {pages}
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={page >= pages}
            onClick={() => {
              setPage(page + 1);
            }}
          >
            Siguiente
          </Button>
        </div>
      )}
      <PreventionSetting />
      {registering && (
        <RegisterRequestDialog
          onClose={() => {
            setRegistering(false);
          }}
        />
      )}
    </Card>
  );
}

function PreventionSetting() {
  const settings = usePrivacySettings();
  const client = useQueryClient();
  const notify = useToast();
  if (!settings.data) return null;
  return (
    <label className="flex min-h-11 items-start gap-3 border-t border-border pt-4 text-sm">
      <input
        type="checkbox"
        className="mt-0.5 size-5"
        checked={settings.data.preventionPausesTerm}
        onChange={(e) => {
          void api('/privacy/settings', {
            method: 'PATCH',
            body: { preventionPausesTerm: e.target.checked },
          })
            .then(() => client.invalidateQueries({ queryKey: requestKeys.settings }))
            .catch((error: unknown) => {
              notify(errorMessage(error), 'error');
            });
        }}
      />
      <span>
        <span className="font-medium">La prevención detiene el plazo</span>
        <span className="block text-muted-foreground">
          Mientras la persona completa lo que falta, no corren los días para responder; al
          completarlo, el plazo sigue con los días que quedaban. Consúltalo con tu abogado.
        </span>
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// One request
// ---------------------------------------------------------------------------

const EVENT_TEXT: Record<string, (data: Record<string, unknown>) => string> = {
  received: () => 'Se registró la recepción',
  taken: () => 'Se tomó para tramitarla',
  contact_linked: (d) =>
    d.contactId ? 'Se vinculó con un contacto' : 'Se desvinculó del contacto',
  prevention_started: (d) =>
    `Prevención: vence el ${formatDueOn(String(d.dueOn))}${d.paused ? ' (el plazo se detiene)' : ''}`,
  prevention_answered: (d) =>
    `Respondió la prevención; ahora vence el ${formatDueOn(String(d.dueOn))}`,
  extended: (d) => `Prórroga: ahora vence el ${formatDueOn(String(d.dueOn))}`,
  identity_verified: (d) =>
    `Identidad verificada: ${IDENTITY_METHODS[d.method as IdentityMethod].toLowerCase()}`,
  resolved: (d) =>
    `Resuelta: ${REQUEST_OUTCOMES[d.outcome as RequestOutcome].toLowerCase()}${d.noData ? ' (no hay datos suyos)' : ''}`,
  data_searched: (d) => {
    const criteria = (d.criteria as RequestSearchCriterion[]).map(
      (c) => REQUEST_SEARCH_CRITERIA[c],
    );
    const matches = Number(d.matches);
    return `Búsqueda en el sistema por ${listText(criteria)}: ${matches === 0 ? 'sin coincidencias' : matches === 1 ? '1 coincidencia' : `${String(matches)} coincidencias`}`;
  },
  withdrawn: () => 'Desistida',
  closed_incomplete: () => 'Cerrada: no se completó la prevención',
  effect_applied: (d) =>
    ({
      rectification_started: 'El contacto quedó «en proceso de rectificación»',
      rectification_ended: 'El contacto dejó de estar «en proceso de rectificación»',
      processing_limited: 'Se limitó el tratamiento del contacto',
      processing_limited_retained: 'Se limitó el tratamiento de lo que la ley obliga a conservar',
      marketing_opt_out: 'El contacto quedó fuera del marketing (consentimiento revocado)',
      export_generated: 'Se generó la exportación de sus datos',
      anonymized: 'Se anonimizó el contacto',
      anonymized_retained:
        'Se anonimizó el contacto, conservando lo que la ley obliga con el tratamiento limitado',
    })[String(d.effect)] ?? 'Se aplicó un cambio al contacto',
};

type Action =
  'prevention' | 'extend' | 'identity' | 'resolve' | 'withdraw' | 'export' | 'anonymize' | 'search';

export function PrivacyRequestPage() {
  const { id = '' } = useParams();
  const request = useRequest(id);
  const { data: session } = useSession();
  const [action, setAction] = useState<Action | null>(null);
  const run = useAction(id);
  if (request.isPending) return <Loading />;
  if (request.error) {
    return (request.error as { status?: number }).status === 404 ? (
      <NotFoundPage />
    ) : (
      <Alert tone="danger">{errorMessage(request.error)}</Alert>
    );
  }
  const r = request.data;
  const tz = session?.tenant.timezone;
  const open = OPEN_STATUSES.includes(r.status);
  const late = r.daysLeft !== null && r.daysLeft < 0;
  const close = () => {
    setAction(null);
  };
  return (
    <div className="space-y-5">
      <div className="space-y-1">
        <Link to="/admin/privacidad" className="text-sm text-primary hover:underline">
          ← Privacidad
        </Link>
        <h1 className="text-2xl font-semibold">
          Solicitud n.º {r.folio}: {PRIVACY_RIGHTS[r.right].label.toLowerCase()}
        </h1>
        <p className="flex flex-wrap items-center gap-2 text-sm">
          <RequestStatusBadge status={r.status} />
          {r.extended && <Badge>Prorrogada</Badge>}
          {open && r.status !== 'in_prevention' && (
            <span className={late ? 'font-medium text-danger' : 'text-muted-foreground'}>
              {daysLeftLabel(r.daysLeft)} · responder antes del {formatDueOn(r.dueOn)}
            </span>
          )}
        </p>
      </div>

      {open && (
        <div className="flex flex-wrap gap-2">
          {r.status === 'received' && (
            <Button variant="secondary" onClick={() => void run('/take', {}, 'Tomada.')}>
              Tomar
            </Button>
          )}
          {r.status === 'in_prevention' ? (
            <Button
              variant="brand"
              onClick={() => void run('/prevention/answered', {}, 'El plazo sigue corriendo.')}
            >
              Registrar que completó lo pedido
            </Button>
          ) : (
            <>
              {r.contact && (r.right === 'access' || r.right === 'portability') && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAction('export');
                  }}
                >
                  Exportar sus datos
                </Button>
              )}
              {r.contact && (r.right === 'cancellation' || r.right === 'erasure') && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAction('anonymize');
                  }}
                >
                  Anonimizar el contacto
                </Button>
              )}
              <Button
                variant="secondary"
                onClick={() => {
                  setAction('search');
                }}
              >
                Buscar en el sistema
              </Button>
              {!r.identity && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAction('identity');
                  }}
                >
                  Verificar identidad
                </Button>
              )}
              {!r.prevention && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAction('prevention');
                  }}
                >
                  Pedir que complete (prevención)
                </Button>
              )}
              {!r.extended && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAction('extend');
                  }}
                >
                  Prorrogar
                </Button>
              )}
              <Button
                variant="brand"
                onClick={() => {
                  setAction('resolve');
                }}
              >
                Resolver
              </Button>
            </>
          )}
          <Button
            variant="ghost"
            onClick={() => {
              setAction('withdraw');
            }}
          >
            Desistió
          </Button>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="space-y-5">
          <Card className="space-y-3">
            <h2 className="font-semibold">Lo que pide</h2>
            <p className="text-sm whitespace-pre-line">{r.description}</p>
            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <Row label="Quién">{r.requesterName}</Row>
              <Row label="Responderle en">{r.replyTo ?? '—'}</Row>
              <Row label="Llegó por">{REQUEST_CHANNELS[r.channel]}</Row>
              <Row label="Recibida">{formatDateTime(r.receivedAt, tz)}</Row>
              <Row label="Registró">{r.registeredBy}</Row>
              <Row label="Contacto">
                {r.contact ? (
                  <Link to={`/contactos/${r.contact.id}`} className="text-primary hover:underline">
                    {r.contact.name}
                  </Link>
                ) : (
                  'Sin vincular'
                )}
              </Row>
              <Row label="Identidad">
                {r.identity ? IDENTITY_METHODS[r.identity.method] : 'Sin verificar'}
              </Row>
            </dl>
          </Card>
          {r.prevention && (
            <Card className="space-y-2">
              <h2 className="font-semibold">Prevención</h2>
              <p className="text-sm whitespace-pre-line">{r.prevention.note}</p>
              <p className="text-sm text-muted-foreground">
                {r.prevention.answeredAt
                  ? `Completó lo pedido el ${formatDateTime(r.prevention.answeredAt, tz)}.`
                  : `Tiene hasta el ${formatDueOn(r.prevention.dueOn)}${r.prevention.paused ? '; mientras tanto el plazo está detenido' : ''}. Si no responde, se cierra como «no completada».`}
              </p>
            </Card>
          )}
          {r.extensionReason && (
            <Card className="space-y-2">
              <h2 className="font-semibold">Prórroga</h2>
              <p className="text-sm">{r.extensionReason}</p>
            </Card>
          )}
          {(r.response ?? r.closingNote) && (
            <Card className="space-y-2">
              <h2 className="font-semibold">
                {r.outcome ? `Resolución: ${REQUEST_OUTCOMES[r.outcome].toLowerCase()}` : 'Cierre'}
              </h2>
              {r.response && <p className="text-sm whitespace-pre-line">{r.response}</p>}
              {r.retentionReason && (
                <p className="text-sm text-muted-foreground">
                  Se conserva por: {r.retentionReason}
                </p>
              )}
              {r.closingNote && <p className="text-sm text-muted-foreground">{r.closingNote}</p>}
            </Card>
          )}
        </div>
        <Card className="space-y-3">
          <h2 className="font-semibold">Historial</h2>
          <ol className="space-y-3 border-l border-border pl-4 text-sm">
            {r.events.map((e, i) => (
              <li key={`${e.kind}-${String(i)}`}>
                <p>{EVENT_TEXT[e.kind]?.(e.data) ?? e.kind}</p>
                <p className="text-xs text-muted-foreground">
                  {formatDateTime(e.at, tz)}
                  {e.by ? ` · ${e.by}` : ''}
                </p>
              </li>
            ))}
          </ol>
        </Card>
      </div>

      {action === 'search' && <SearchDialog request={r} onClose={close} />}
      {action === 'export' && r.contact && (
        <ExportDialog contact={r.contact} requestId={r.id} onClose={close} />
      )}
      {action === 'anonymize' && r.contact && (
        <AnonymizeDialog contact={r.contact} requestId={r.id} onClose={close} />
      )}
      {action === 'prevention' && (
        <TextDialog
          title="Pedir que complete la solicitud"
          description={`Una sola vez. Tendrá ${String(REQUEST_PREVENTION_DAYS)} días hábiles para completarla; si no, se cierra como «no completada».`}
          label="Qué falta"
          confirm="Registrar prevención"
          onClose={close}
          onConfirm={(note) => run('/prevention', { note }, 'Prevención registrada.')}
        />
      )}
      {action === 'extend' && (
        <TextDialog
          title="Prorrogar"
          description={`Una sola vez: ${String(REQUEST_EXTENSION_DAYS)} días hábiles más. La justificación queda en el registro.`}
          label="Justificación"
          confirm="Prorrogar"
          onClose={close}
          onConfirm={(reason) => run('/extend', { reason }, 'Plazo prorrogado.')}
        />
      )}
      {action === 'withdraw' && (
        <TextDialog
          title="Registrar que desistió"
          label="Nota (opcional)"
          optional
          confirm="Cerrar como desistida"
          onClose={close}
          onConfirm={(note) => run('/withdraw', note ? { note } : {}, 'Solicitud cerrada.')}
        />
      )}
      {action === 'identity' && <IdentityDialog onClose={close} run={run} />}
      {action === 'resolve' && <ResolveDialog request={r} onClose={close} run={run} />}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  );
}

type Run = (path: string, body: unknown, done: string) => Promise<boolean>;

function useAction(id: string): Run {
  const client = useQueryClient();
  const notify = useToast();
  return async (path, body, done) => {
    try {
      const saved = await api<RequestDetail>(`/privacy/requests/${id}${path}`, {
        method: 'POST',
        body,
      });
      client.setQueryData(requestKeys.detail(id), saved);
      await refreshRequests(client);
      notify(done, 'success');
      return true;
    } catch (error) {
      notify(
        error instanceof ApiError && error.issues.length > 0
          ? (error.issues[0]?.message ?? errorMessage(error))
          : errorMessage(error),
        'error',
      );
      return false;
    }
  };
}

function TextDialog({
  title,
  description,
  label,
  confirm,
  optional = false,
  onClose,
  onConfirm,
}: {
  title: string;
  description?: string;
  label: string;
  confirm: string;
  optional?: boolean;
  onClose: () => void;
  onConfirm: (text: string) => Promise<boolean>;
}) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  return (
    <Dialog open onClose={onClose} title={title} {...(description ? { description } : {})}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          setSaving(true);
          void onConfirm(text.trim()).then((ok) => {
            setSaving(false);
            if (ok) onClose();
          });
        }}
      >
        <Field label={label}>
          {(a11y) => (
            <textarea
              {...a11y}
              rows={3}
              maxLength={2000}
              className={textarea}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
              }}
            />
          )}
        </Field>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="brand"
            loading={saving}
            disabled={!optional && !text.trim()}
          >
            {confirm}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function IdentityDialog({ onClose, run }: { onClose: () => void; run: Run }) {
  const [method, setMethod] = useState<IdentityMethod | ''>('');
  return (
    <Dialog
      open
      onClose={onClose}
      title="Verificar la identidad"
      description="Antes de entregar o cambiar datos, confirma que quien pide es el titular (o su representante)."
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!method) return;
          void run('/identity', { method }, 'Identidad verificada.').then((ok) => {
            if (ok) onClose();
          });
        }}
      >
        <fieldset className="space-y-1">
          <legend className="mb-1 text-sm font-medium">¿Cómo la verificaste?</legend>
          {(Object.keys(IDENTITY_METHODS) as IdentityMethod[]).map((m) => (
            <label key={m} className="flex min-h-11 items-center gap-3 text-sm">
              <input
                type="radio"
                name="identity"
                className="size-5"
                checked={method === m}
                onChange={() => {
                  setMethod(m);
                }}
              />
              {IDENTITY_METHODS[m]}
            </label>
          ))}
        </fieldset>
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" variant="brand" disabled={!method}>
            Confirmar
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** What each outcome does to the contact, so nobody resolves blindly. */
function effectText(request: RequestDetail, outcome: RequestOutcome | ''): string | null {
  if (!outcome || !request.contact) return null;
  const proceeds = outcome !== 'denied';
  switch (request.right) {
    case 'rectification':
      return 'El contacto deja de estar «en proceso de rectificación». Corrige sus datos antes de resolver.';
    case 'limitation':
      return proceeds ? 'Se limitará el tratamiento del contacto: solo se conserva.' : null;
    case 'opposition':
      return proceeds
        ? 'El contacto quedará fuera del marketing y se revocará ese consentimiento.'
        : null;
    case 'cancellation':
    case 'erasure':
      return outcome === 'partially_retained'
        ? 'Lo que la ley obliga a conservar quedará con el tratamiento limitado, con el motivo.'
        : null;
    default:
      return null;
  }
}

function ResolveDialog({
  request,
  onClose,
  run,
}: {
  request: RequestDetail;
  onClose: () => void;
  run: Run;
}) {
  const [outcome, setOutcome] = useState<RequestOutcome | ''>('');
  const [response, setResponse] = useState('');
  const [reason, setReason] = useState('');
  const [noData, setNoData] = useState(false);
  const [saving, setSaving] = useState(false);
  const effect = effectText(request, outcome);
  const needsIdentity = outcome !== '' && outcome !== 'denied' && !request.identity;
  // Without a contact, "procede" only when the agency holds nothing about the person.
  const searched = request.events.some((e) => e.kind === 'data_searched');
  const needsContact = outcome !== '' && outcome !== 'denied' && !request.contact && !noData;
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Resolver la solicitud n.º ${String(request.folio)}`}
      description="Registra la respuesta que se envió a la persona."
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!outcome) return;
          setSaving(true);
          void run(
            '/resolve',
            {
              outcome,
              response,
              ...(outcome === 'partially_retained' ? { retentionReason: reason } : {}),
              ...(noData && !request.contact ? { noData: true } : {}),
            },
            'Solicitud resuelta.',
          ).then((ok) => {
            setSaving(false);
            if (ok) onClose();
          });
        }}
      >
        <Field label="Resolución">
          {(a11y) => (
            <Select
              {...a11y}
              value={outcome}
              onChange={(e) => {
                setOutcome(e.target.value as RequestOutcome);
              }}
            >
              <option value="">Elige…</option>
              {(Object.keys(REQUEST_OUTCOMES) as RequestOutcome[]).map((o) => (
                <option key={o} value={o}>
                  {REQUEST_OUTCOMES[o]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {outcome === 'partially_retained' && (
          <Field label="Qué obligación legal exige conservar los datos">
            {(a11y) => (
              <textarea
                {...a11y}
                rows={2}
                maxLength={500}
                className={textarea}
                value={reason}
                onChange={(e) => {
                  setReason(e.target.value);
                }}
              />
            )}
          </Field>
        )}
        <Field label="Respuesta enviada">
          {(a11y) => (
            <textarea
              {...a11y}
              rows={4}
              maxLength={5000}
              className={textarea}
              value={response}
              onChange={(e) => {
                setResponse(e.target.value);
              }}
            />
          )}
        </Field>
        {!request.contact && (
          <label className="flex min-h-11 items-start gap-3 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 size-5"
              checked={noData}
              disabled={!searched}
              onChange={(e) => {
                setNoData(e.target.checked);
              }}
            />
            <span>
              <span className="font-medium">Verifiqué que no hay datos suyos</span>
              <span className="block text-muted-foreground">
                {searched
                  ? 'La búsqueda en el sistema queda en el historial.'
                  : 'Primero búscala en el sistema (nombre, teléfono, correo y DUI).'}
              </span>
            </span>
          </label>
        )}
        {effect && <Alert tone="info">{effect}</Alert>}
        {needsContact && (
          <Alert tone="warning">
            Vincula el contacto de la persona con «Buscar en el sistema» o, si no hay datos suyos,
            márcalo arriba.
          </Alert>
        )}
        {needsIdentity && (
          <Alert tone="warning">Primero verifica la identidad de quien lo pide.</Alert>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="brand"
            loading={saving}
            disabled={!outcome || !response.trim() || needsIdentity || needsContact}
          >
            Resolver
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

/** "a", "a y b", "a, b y c". */
function listText(items: string[]): string {
  return items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} y ${items[items.length - 1] ?? ''}`;
}

/**
 * Searches the whole agency for the person: to link their contact, or to be able to
 * answer that the agency holds no data about them (the history keeps which criteria
 * were used, not the values).
 */
function SearchDialog({ request, onClose }: { request: RequestDetail; onClose: () => void }) {
  const client = useQueryClient();
  const notify = useToast();
  const [values, setValues] = useState({
    name: request.contact?.name ?? request.requesterName,
    phone: '',
    email: '',
    nationalId: '',
  });
  const [matches, setMatches] = useState<RequestSearchMatch[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = (detail: RequestDetail) => {
    client.setQueryData(requestKeys.detail(request.id), detail);
    return refreshRequests(client);
  };
  const search = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await api<{ matches: RequestSearchMatch[]; request: RequestDetail }>(
        `/privacy/requests/${request.id}/search`,
        { method: 'POST', body: values },
      );
      setMatches(result.matches);
      await save(result.request);
    } catch (e) {
      setError(
        e instanceof ApiError && e.issues.length > 0
          ? (e.issues[0]?.message ?? errorMessage(e))
          : errorMessage(e),
      );
    } finally {
      setSaving(false);
    }
  };
  const link = async (contactId: string) => {
    try {
      await save(
        await api<RequestDetail>(`/privacy/requests/${request.id}/contact`, {
          method: 'PATCH',
          body: { contactId },
        }),
      );
      notify('Contacto vinculado.', 'success');
      onClose();
    } catch (e) {
      notify(errorMessage(e), 'error');
    }
  };
  const field = (key: keyof typeof values, label: string, type = 'text') => (
    <Field label={label}>
      {(a11y) => (
        <Input
          {...a11y}
          type={type}
          autoComplete="off"
          value={values[key]}
          onChange={(e) => {
            setValues({ ...values, [key]: e.target.value });
          }}
        />
      )}
    </Field>
  );
  return (
    <Dialog
      open
      onClose={onClose}
      title="Buscar a la persona en el sistema"
      description="En toda la inmobiliaria, también en la papelera. El historial guarda qué datos se usaron, no los valores."
    >
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          void search();
        }}
      >
        {field('name', 'Nombre')}
        <div className="grid gap-4 sm:grid-cols-3">
          {field('phone', 'Teléfono', 'tel')}
          {field('email', 'Correo', 'email')}
          {field('nationalId', 'DUI')}
        </div>
        {error && <Alert tone="danger">{error}</Alert>}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
          <Button type="submit" variant="brand" loading={saving} disabled={!values.name.trim()}>
            Buscar
          </Button>
        </div>
      </form>
      {matches && (
        <div className="mt-4 space-y-2 border-t border-border pt-4">
          <h3 className="text-sm font-medium">Resultado</h3>
          {matches.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No hay ningún contacto con esos datos. Si al resolver no procede vincular a nadie,
              marca «Verifiqué que no hay datos suyos».
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {matches.map((m) => (
                <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="text-sm">
                    <span className="font-medium">{m.name}</span>
                    <span className="block text-muted-foreground">
                      Coincide por {listText(m.matchedBy.map((c) => REQUEST_SEARCH_CRITERIA[c]))}
                      {m.inTrash ? ' · en la papelera' : ''}
                      {m.anonymized ? ' · anonimizado' : ''}
                    </span>
                  </span>
                  {request.contact?.id === m.id ? (
                    <Badge>Vinculado</Badge>
                  ) : (
                    !m.inTrash && (
                      <Button size="sm" variant="secondary" onClick={() => void link(m.id)}>
                        Vincular
                      </Button>
                    )
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Dialog>
  );
}
